import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import './index.css';

/**
 * Strip the signature off any Supabase storage URL before it leaves the
 * browser.
 *
 * Signed URLs are bearer tokens: for an hour, anyone holding one can fetch the
 * object without a session. With the buckets private they point at driver
 * documents and invoices, so a breadcrumb, span, log, replay or console event
 * carrying `?token=` would turn Sentry access into document access, for longer
 * than the token lives.
 */
function scrubStorageToken(value: string): string {
  // Not conditioned on the storage path: fetch/XHR spans also carry the query
  // string on its own (`http.query: "?token=…"`).
  if (!/[?&]token=/i.test(value)) return value;
  return value.replace(/([?&])token=[^&#\s"']*/gi, '$1token=REDACTED');
}

/**
 * Scrubs every string in a Sentry payload to a bounded depth. Copy-on-write:
 * a container is copied only when something inside it changed, so objects the
 * app passed to console.* (breadcrumb arguments) are never mutated. Class
 * instances (DOM nodes, events, Errors) are left alone: walking them is
 * expensive and writing to them can throw.
 */
function scrubDeep<T>(value: T, depth = 0): T {
  if (typeof value === 'string') return scrubStorageToken(value) as T;
  if (depth > 6 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    let copy: unknown[] | null = null;
    value.forEach((item, i) => {
      const scrubbed = scrubDeep(item, depth + 1);
      if (scrubbed !== item) {
        copy ??= [...value];
        copy[i] = scrubbed;
      }
    });
    return (copy ?? value) as T;
  }
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;
  let copy: Record<string, unknown> | null = null;
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    const scrubbed = scrubDeep(item, depth + 1);
    if (scrubbed !== item) {
      copy ??= { ...(value as Record<string, unknown>) };
      copy[key] = scrubbed;
    }
  }
  return (copy ?? value) as T;
}

// rrweb's EventType.Custom: replay breadcrumbs and network spans (with URLs).
// DOM snapshots are covered by `block` below and are too large to walk.
const RRWEB_CUSTOM_EVENT = 5;

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.PROD, // never runs on localhost / vite dev
  environment: import.meta.env.MODE, // "production" in a real build
  sendDefaultPii: true,
  // captureConsoleIntegration turns console.warn/error into events, so error
  // events need scrubbing too, not just breadcrumbs and transactions.
  beforeBreadcrumb: breadcrumb => scrubDeep(breadcrumb),
  beforeSend: event => scrubDeep(event),
  beforeSendTransaction: event => scrubDeep(event),
  beforeSendSpan: span => scrubDeep(span),
  beforeSendLog: log => scrubDeep(log),
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // maskAllText masks text nodes, not attributes, so the replay DOM would
      // still carry `?token=` in href/src without these.
      block: [
        'a[href*="/storage/v1/object/sign/"]',
        'img[src*="/storage/v1/object/sign/"]',
        'iframe[src*="/storage/v1/object/sign/"]',
        'embed[src*="/storage/v1/object/sign/"]',
        'object[data*="/storage/v1/object/sign/"]',
      ],
      beforeAddRecordingEvent: event =>
        (event as { type?: number }).type === RRWEB_CUSTOM_EVENT ? scrubDeep(event) : event,
    }),
    Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
  enableLogs: true,
  // OSRM's free demo routing server flakes intermittently; these are expected
  // fallbacks to a straight-line route, not real errors. Drop them so they
  // don't burn Sentry quota or trigger alerts.
  ignoreErrors: [
    /Failed to fetch route/i,
    /Error fetching (driving )?route/i,
    /Route unavailable/i,
  ],
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>
);
